import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, ArrowLeft, Send } from 'lucide-react';
import containerImage from '../assets/images/container-illustration.png';
import { authAPI } from '../services/api';
import TermsAcceptanceModal from '../components/legal/TermsAcceptanceModal';
import { needsAcceptance } from '../legal';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const submitRegistration = async () => {
    setIsLoading(true);
    try {
      await authAPI.register({ email });
      setEmailSent(true);
      setSuccess(true);
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Failed to send verification email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Gate registration on Terms + Privacy acceptance for the current versions.
    if (needsAcceptance(['web_tos', 'privacy_policy'])) {
      setPendingSubmit(true);
      setTermsOpen(true);
      return;
    }

    await submitRegistration();
  };

  const handleGoogleSignUp = () => {
    if (needsAcceptance(['web_tos', 'privacy_policy'])) {
      setPendingSubmit(false);  // Google flow doesn't go through submitRegistration
      setTermsOpen(true);
      // Stash intent so onAccept can resume the Google flow.
      (window as unknown as { __pendingGoogle?: boolean }).__pendingGoogle = true;
      return;
    }
    authAPI.googleAuth({ intent: 'business' });
  };

  const handleTermsAccept = async () => {
    setTermsOpen(false);
    if ((window as unknown as { __pendingGoogle?: boolean }).__pendingGoogle) {
      (window as unknown as { __pendingGoogle?: boolean }).__pendingGoogle = false;
      authAPI.googleAuth({ intent: 'business' });
      return;
    }
    if (pendingSubmit) {
      setPendingSubmit(false);
      await submitRegistration();
    }
  };

  const handleTermsDecline = () => {
    setTermsOpen(false);
    setPendingSubmit(false);
    (window as unknown as { __pendingGoogle?: boolean }).__pendingGoogle = false;
    setError('You must accept the Terms of Service and Privacy Policy to register.');
  };

  const handleResendEmail = async () => {
    setIsLoading(true);
    setError('');

    try {
      await authAPI.resendVerification(email);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Failed to resend verification email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Logo - Top Left */}
      <div className="absolute top-6 left-6 z-10">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="EAloom" className="w-7 h-7 rounded-md" />
          <h1 className="text-lg font-semibold text-gray-900">EAloom</h1>
        </div>
      </div>

      {/* Copyright - Bottom Left */}
      <div className="absolute bottom-6 left-6 z-10">
        <p className="text-xs text-gray-500 leading-relaxed">&copy; 2026 EAloom. All rights reserved.</p>
        <p className="text-xs text-gray-400 leading-relaxed">EAloom is owned and operated by Lambda Flow LLC.</p>
      </div>

      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-20 xl:px-28">
        <div className="w-full max-w-sm">
          {emailSent ? (
            /* Email Sent State */
            <div>
              <div className="mb-8">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-green-600" aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Check your email</h2>
                <p className="text-gray-600">
                  We've sent a verification link to:
                </p>
                <p className="font-semibold text-gray-900 mt-1">{email}</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  Please check your inbox and click the verification link to complete your registration.
                  The link will expire in 24 hours.
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  type="button"
                  onClick={handleResendEmail}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full h-11 border-green-600 text-green-600 hover:bg-green-50 font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  {isLoading ? 'Sending\u2026' : 'Resend Verification Email'}
                </Button>
              </div>

              <div aria-live="polite" aria-atomic="true">
                {success && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">Verification email resent successfully!</p>
                  </div>
                )}

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
              </div>

              <Link
                to="/login"
                className="w-full text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center justify-center gap-2 mt-6"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Back to log in
              </Link>
            </div>
          ) : (
            /* Registration Form State */
            <div>
              <div className="mb-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-gray-600" aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Create account</h2>
                <p className="text-gray-600">Enter your email to get started.</p>
              </div>

              <div aria-live="polite" aria-atomic="true">
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email address
                  </label>
                  <div className="relative rounded-lg border-2 border-gray-300 focus-within:border-green-500 transition-colors">
                    <Input
                      id="register-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      spellCheck={false}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email…"
                      className="w-full h-11 px-3 border-0 bg-transparent focus:ring-0 focus:outline-none rounded-lg"
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    'Sending\u2026'
                  ) : (
                    <>
                      Send Verification Email
                      <Send className="w-4 h-4" aria-hidden="true" />
                    </>
                  )}
                </Button>

                {/* Divider with OR */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>

                {/* Google sign up — default intent=business (tenant boss).
                    User can still switch to Dispatcher/Employee tab on the
                    fill-information screen; intent only controls whether the
                    OAuth backend is allowed to create a new placeholder user. */}
                <Button
                  type="button"
                  onClick={handleGoogleSignUp}
                  variant="outline"
                  className="w-full h-11 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign up with Google
                </Button>

                {/* Sign in link */}
                <div className="text-center">
                  <span className="text-sm text-gray-600">Already have an account? </span>
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Container image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
        <div className="flex items-center justify-center w-full h-full relative">
          <img
            src={containerImage}
            alt="Container Illustration"
            className="w-full h-full max-w-none max-h-none object-contain scale-150"
          />
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-32 left-20 w-24 h-24 bg-blue-200/20 rounded-full blur-lg"></div>
      </div>

      <TermsAcceptanceModal
        open={termsOpen}
        email={email}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
        blocking={false}
      />
    </div>
  );
};

export default Register;
